// Per-project branding for the hosted auth-ui. Resolves the project's
// client_id from the URL (?client_id=...), fetches /api/auth/branding,
// and writes the resolved color tokens to <style> overrides scoped to
// `:root[data-theme="light"]` and `:root[data-theme="dark"]` so the active
// theme (driven by the `data-theme` attribute on <html>) picks up the
// right palette.
//
// Theme resolution precedence (highest first):
//   1. URL ?theme=light|dark|auto  ← app-driven, per-redirect
//   2. localStorage pryma-auth-theme:<clientId>  ← only the optional end-user
//      Sun/Moon toggle writes here; admins must opt the project in via
//      `allow_user_theme_override` (off by default)
//   3. project default_theme
//   4. prefers-color-scheme (when the effective theme above is 'auto')
//   5. light
//
// Note: the URL param is NEVER persisted to localStorage. It is the consuming
// app's per-session declaration of intent; persisting it would silently
// override the app on a subsequent visit that doesn't include `?theme=`.
//
// Failure mode: silent fallback. The default tokens in index.css ship clean
// light and dark themes, so a 4xx/5xx/timeout never blocks sign-in — the UI
// just renders with the platform defaults.

import { useCallback, useEffect, useState } from 'react';
import {
  BRANDING_COLOR_KEYS,
  getBranding,
  type BrandingColorKey,
  type BrandingLayout,
  type BrandingPayload,
  type BrandingTheme,
  type PublicAuthMethods,
} from './api';

// Local runtime list for narrowing user input (URL params, localStorage).
// Kept local rather than imported so page-level tests that vi.mock('./api')
// with a narrow mock don't have to re-export every runtime constant.
const THEME_VALUES: readonly BrandingTheme[] = ['light', 'dark', 'auto'] as const;
function isBrandingTheme(value: unknown): value is BrandingTheme {
  return typeof value === 'string' && (THEME_VALUES as readonly string[]).includes(value);
}

// Map the API's snake_case color keys to the kebab-case CSS custom property
// names the stylesheet consumes. Single source of truth for this mapping.
const COLOR_TO_CSS_VAR: Record<BrandingColorKey, string> = {
  primary_color: '--brand-primary',
  primary_hover_color: '--brand-primary-hover',
  accent_color: '--brand-accent',
  background_color: '--brand-background',
  foreground_color: '--brand-foreground',
  error_color: '--brand-error',
};

// Conservative defaults used when no client_id is in the URL or the fetch
// fails. email_password=true keeps the form visible so a misconfigured link
// still renders a sign-in surface; the API will surface the project error
// when the form is submitted.
export const DEFAULT_AUTH_METHODS: PublicAuthMethods = {
  email_password: true,
  magic_link: false,
  email_otp: false,
  oauth_providers: [],
};

export type EffectiveTheme = 'light' | 'dark';

export interface ResolvedBranding {
  logoUrl: string | null;
  appName: string | null;
  authMethods: PublicAuthMethods;
  layout: BrandingLayout;
  defaultTheme: BrandingTheme;
  allowUserThemeOverride: boolean;
}

const EMPTY_BRANDING: ResolvedBranding = {
  logoUrl: null,
  appName: null,
  authMethods: DEFAULT_AUTH_METHODS,
  layout: 'centered',
  defaultTheme: 'light',
  // Match the API default — the end-user toggle is opt-in, off by default.
  allowUserThemeOverride: false,
};

// ID for the injected per-theme color overrides <style> tag. Re-used so
// hot-reloads in dev don't pile up duplicate <style> nodes.
const BRANDING_STYLE_ELEMENT_ID = 'auth-ui-branding-overrides';

const THEME_STORAGE_KEY_PREFIX = 'pryma-auth-theme';

// Read the OAuth-flavor `client_id` query param from the current URL, which
// in our hosted-login flow is the project UUID. Returns null when missing.
export function getClientIdFromLocation(search: string = window.location.search): string | null {
  const params = new URLSearchParams(search);
  const value = params.get('client_id');
  return value && value.length > 0 ? value : null;
}

// Read the ?theme=... override the consumer can pass on the auth redirect.
// Returns null for unknown values so the cascade can fall through.
export function getThemeFromLocation(
  search: string = window.location.search
): BrandingTheme | null {
  const params = new URLSearchParams(search);
  const value = params.get('theme');
  return isBrandingTheme(value) ? value : null;
}

function themeStorageKey(clientId: string | null): string {
  return clientId ? `${THEME_STORAGE_KEY_PREFIX}:${clientId}` : THEME_STORAGE_KEY_PREFIX;
}

// Read a previously-persisted user override. Only honored when the project
// allows user theme override; otherwise we discard the value.
export function getStoredTheme(clientId: string | null): BrandingTheme | null {
  try {
    const value = window.localStorage.getItem(themeStorageKey(clientId));
    return isBrandingTheme(value) ? value : null;
  } catch {
    return null;
  }
}

function storeUserTheme(clientId: string | null, theme: BrandingTheme): void {
  try {
    window.localStorage.setItem(themeStorageKey(clientId), theme);
  } catch {
    /* localStorage may be unavailable (Safari private mode etc.) — skip. */
  }
}

function getSystemTheme(): EffectiveTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Resolve the effective light/dark theme to apply right now.
//
// Precedence (highest first):
//   1. urlTheme   — app-driven, ephemeral per-session
//   2. userTheme  — only honored when the project enables the end-user toggle
//   3. project default_theme
// 'auto' at the top of that cascade resolves to the system theme.
export function resolveEffectiveTheme(
  branding: ResolvedBranding,
  userTheme: BrandingTheme | null,
  systemTheme: EffectiveTheme,
  urlTheme: BrandingTheme | null = null
): EffectiveTheme {
  const desired: BrandingTheme =
    urlTheme ?? (branding.allowUserThemeOverride && userTheme ? userTheme : branding.defaultTheme);

  if (desired === 'auto') return systemTheme;
  return desired;
}

// Build a CSS string of `:root[data-theme="..."] { --brand-x: value; }` blocks
// for the colors the project actually customized. Tokens with null values are
// left out so the stylesheet defaults apply.
function buildBrandingCss(payload: BrandingPayload): string {
  const blocks: string[] = [];

  const lightDecls = BRANDING_COLOR_KEYS.flatMap(key => {
    const value = payload.colors[key];
    return value ? [`${COLOR_TO_CSS_VAR[key]}: ${value};`] : [];
  });
  if (lightDecls.length > 0) {
    blocks.push(`:root[data-theme="light"] {\n  ${lightDecls.join('\n  ')}\n}`);
  }

  const darkDecls = BRANDING_COLOR_KEYS.flatMap(key => {
    const value = payload.colors_dark[key];
    return value ? [`${COLOR_TO_CSS_VAR[key]}: ${value};`] : [];
  });
  if (darkDecls.length > 0) {
    blocks.push(`:root[data-theme="dark"] {\n  ${darkDecls.join('\n  ')}\n}`);
  }

  return blocks.join('\n');
}

// Inject (or replace) the per-theme color overrides in a single <style> tag.
// We don't write directly to `:root` inline styles anymore because we need
// two parallel value sets keyed by `data-theme`.
function injectBrandingStyles(css: string, doc: Document = document): void {
  const existing = doc.getElementById(BRANDING_STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!css) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.textContent = css;
    return;
  }
  const style = doc.createElement('style');
  style.id = BRANDING_STYLE_ELEMENT_ID;
  style.textContent = css;
  doc.head.appendChild(style);
}

function setActiveThemeAttribute(theme: EffectiveTheme, doc: Document = document): void {
  doc.documentElement.setAttribute('data-theme', theme);
}

// Apply a branding payload: inject color overrides, set the page title, and
// return a ResolvedBranding for consumers. Does NOT set the active theme
// attribute — that happens in the hook after resolving the cascade.
export function applyBrandingToDocument(
  payload: BrandingPayload,
  doc: Document = document
): ResolvedBranding {
  injectBrandingStyles(buildBrandingCss(payload), doc);

  if (payload.app_name) {
    doc.title = `Sign in to ${payload.app_name}`;
  }

  return {
    logoUrl: payload.logo_url,
    appName: payload.app_name,
    authMethods: payload.auth_methods ?? DEFAULT_AUTH_METHODS,
    layout: payload.layout ?? 'centered',
    defaultTheme: payload.default_theme ?? 'light',
    allowUserThemeOverride: payload.allow_user_theme_override ?? true,
  };
}

// State machine: 'pending' until the network resolves (or until we decide
// there is no client_id to look up). 'ready' once branding has been applied
// (with defaults if there was no client_id or the fetch failed). The auth-ui
// renders a neutral skeleton in the 'pending' state.
export type BrandingStatus = 'pending' | 'ready';

export interface UseBrandingResult {
  status: BrandingStatus;
  branding: ResolvedBranding;
  effectiveTheme: EffectiveTheme;
  // When `allow_user_theme_override` is false this is a no-op (the caller
  // shouldn't render a toggle anyway).
  setUserTheme: (theme: BrandingTheme) => void;
  userTheme: BrandingTheme | null;
}

export function useBranding(): UseBrandingResult {
  const [status, setStatus] = useState<BrandingStatus>('pending');
  const [branding, setBranding] = useState<ResolvedBranding>(EMPTY_BRANDING);
  const [systemTheme, setSystemTheme] = useState<EffectiveTheme>(() =>
    typeof window === 'undefined' ? 'light' : getSystemTheme()
  );

  const clientId = typeof window === 'undefined' ? null : getClientIdFromLocation();

  // URL ?theme= is the consuming app's per-session declaration and never
  // touches localStorage — persisting it would silently shadow the app on a
  // later visit that omits the param. localStorage is reserved for the
  // optional user-toggle path below.
  const urlTheme = typeof window === 'undefined' ? null : getThemeFromLocation();
  const [userTheme, setUserTheme] = useState<BrandingTheme | null>(() =>
    typeof window === 'undefined' ? null : getStoredTheme(clientId)
  );

  // Fetch + apply branding.
  useEffect(() => {
    let cancelled = false;

    if (!clientId) {
      setStatus('ready');
      return;
    }

    getBranding(clientId)
      .then(result => {
        if (cancelled) return;
        if (result.success && result.data) {
          const resolved = applyBrandingToDocument(result.data);
          setBranding(resolved);
        }
      })
      .catch(() => {
        // Silent fallback — auth must still load on a branding outage.
      })
      .finally(() => {
        if (!cancelled) setStatus('ready');
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Track system theme so an 'auto' resolution flips with the OS toggle.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
    // Safari < 14 used the deprecated addListener/removeListener; modern
    // browsers expose addEventListener. Both are typed today, use the
    // standard API.
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const effectiveTheme = resolveEffectiveTheme(branding, userTheme, systemTheme, urlTheme);

  // Write the active theme attribute to <html> whenever it changes. Doing
  // this in an effect (vs. in render) keeps the hook free of side effects
  // when called from tests that don't have a DOM.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    setActiveThemeAttribute(effectiveTheme);
  }, [effectiveTheme]);

  const updateUserTheme = useCallback(
    (theme: BrandingTheme) => {
      if (!branding.allowUserThemeOverride) return;
      storeUserTheme(clientId, theme);
      setUserTheme(theme);
    },
    [branding.allowUserThemeOverride, clientId]
  );

  return {
    status,
    branding,
    effectiveTheme,
    setUserTheme: updateUserTheme,
    userTheme,
  };
}
